import { useEffect, useMemo, useRef, useState } from 'react';
import TextBox from 'devextreme-react/text-box';
import NumberBox from 'devextreme-react/number-box';
import SelectBox from 'devextreme-react/select-box';
import Button from 'devextreme-react/button';
import { newToast } from '../../../components';

const VAR_REGEX = /\[([^\]]+)\]/g;

// Operadores: se muestra un símbolo amigable pero se inserta la sintaxis real que
// acepta el backend. La elevación usa Pow( base , exponente ) — NO el símbolo ^.
const OPERATORS = [
  { label: '+', text: ' + ' },
  { label: '−', text: ' - ' },
  { label: '×', text: ' * ' },
  { label: '÷', text: ' / ' },
  { label: '( )', text: '( )', caretFromEnd: 1 },
  { label: 'Pow', text: 'Pow( , )', caretFromEnd: 4, hint: 'Elevación: Pow(base, exponente)' },
];

// Comparadores (se usan en reglas de Resultados propios).
const COMPARATORS = [
  { label: '>', text: ' > ' },
  { label: '<', text: ' < ' },
  { label: '≥', text: ' >= ' },
  { label: '≤', text: ' <= ' },
];

// Funciones del motor de fórmulas (NCalc / .NET), con su capitalización exacta.
// Insertan la plantilla con el cursor en el primer argumento.
const FUNCTIONS = [
  { name: 'Abs', tpl: 'Abs( )', caretFromEnd: 2, hint: 'Valor absoluto' },
  { name: 'Sqrt', tpl: 'Sqrt( )', caretFromEnd: 2, hint: 'Raíz cuadrada' },
  { name: 'Round', tpl: 'Round( , )', caretFromEnd: 4, hint: 'Round(valor, decimales)' },
  { name: 'Truncate', tpl: 'Truncate( )', caretFromEnd: 2, hint: 'Parte entera' },
  { name: 'Log', tpl: 'Log( , )', caretFromEnd: 4, hint: 'Log(valor, base)' },
  { name: 'Ln', tpl: 'Ln( )', caretFromEnd: 2, hint: 'Logaritmo natural' },
  { name: 'Exp', tpl: 'Exp( )', caretFromEnd: 2, hint: 'Exponencial' },
  { name: 'Min', tpl: 'Min( , )', caretFromEnd: 4, hint: 'Mínimo' },
  { name: 'Max', tpl: 'Max( , )', caretFromEnd: 4, hint: 'Máximo' },
  { name: 'if', tpl: 'if( , , )', caretFromEnd: 6, hint: 'if(condición, verdadero, falso)' },
];

interface AnalyteLite {
  An_Id: number;
  An_Abreviatura: string;
  An_Nombre: string;
}

interface FormulaBuilderProps {
  value: string;
  onValueChange: (value: string) => void;
  /** Lista de analitos ya cargada en memoria (para buscar abreviaturas por ID sin llamadas extra). */
  analytes?: AnalyteLite[];
  /** Muestra los comparadores (>, <, >=, <=), usados en Resultados propios. */
  showComparators?: boolean;
}

export const FormulaBuilder = ({ value, onValueChange, analytes = [], showComparators = false }: FormulaBuilderProps) => {
  const taRef = useRef<HTMLTextAreaElement>(null);
  const [abbr, setAbbr] = useState('');
  const [searchId, setSearchId] = useState<number | null>(null);
  // Estado local como fuente de verdad: escribir y pegar es síncrono, así el cursor
  // no salta al final (evita el rebote asíncrono a través del formulario de DevExtreme).
  const [text, setText] = useState(value ?? '');

  const update = (next: string) => {
    setText(next);
    onValueChange(next);
  };

  useEffect(() => {
    // Al montar, deja el cursor al final para que las inserciones se agreguen al final.
    const el = taRef.current;
    if (el) {
      const len = text.length;
      el.selectionStart = el.selectionEnd = len;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const variables = useMemo(() => {
    const set = new Set<string>();
    let m: RegExpExecArray | null;
    VAR_REGEX.lastIndex = 0;
    while ((m = VAR_REGEX.exec(text)) !== null) set.add(m[1]);
    return Array.from(set);
  }, [text]);

  const balanced = useMemo(() => {
    let depth = 0;
    for (const ch of text) {
      if (ch === '(') depth++;
      else if (ch === ')') {
        depth--;
        if (depth < 0) return false;
      }
    }
    return depth === 0;
  }, [text]);

  const insert = (snippet: string, caretFromEnd = 0) => {
    const el = taRef.current;
    const start = el ? el.selectionStart : text.length;
    const end = el ? el.selectionEnd : text.length;
    const next = text.slice(0, start) + snippet + text.slice(end);
    update(next);
    const pos = start + snippet.length - caretFromEnd;
    requestAnimationFrame(() => {
      if (el) {
        el.focus();
        el.setSelectionRange(pos, pos);
      }
    });
  };

  const insertVariable = () => {
    const clean = abbr.trim().toUpperCase().replace(/[[\]]/g, '');
    if (!clean) {
      newToast('WARNING', 'Escribe la abreviatura del analito', 3);
      return;
    }
    insert(`[${clean}]`);
    setAbbr('');
  };

  // Índice por ID sobre la lista ya cargada en memoria (sin llamadas al backend).
  const byId = useMemo(() => {
    const map = new Map<number, AnalyteLite>();
    analytes.forEach((a) => map.set(a.An_Id, a));
    return map;
  }, [analytes]);

  const searchAndInsert = () => {
    if (searchId == null) {
      newToast('WARNING', 'Escribe el ID del analito', 3);
      return;
    }
    const found = byId.get(Number(searchId));
    if (!found) {
      newToast('WARNING', `No se encontró un analito con ID ${searchId}`, 4);
      return;
    }
    const clean = (found.An_Abreviatura ?? '').trim().toUpperCase().replace(/[[\]]/g, '');
    if (!clean) {
      newToast('WARNING', `El analito ${found.An_Nombre} no tiene abreviatura configurada`, 4);
      return;
    }
    insert(`[${clean}]`);
    newToast('SUCCESS', `Insertado [${clean}] — ${found.An_Nombre}`, 3);
    setSearchId(null);
  };

  const copyFormula = async () => {
    const formula = text;
    if (!formula.trim()) {
      newToast('WARNING', 'La fórmula está vacía', 3);
      return;
    }
    try {
      await navigator.clipboard.writeText(formula);
      newToast('SUCCESS', 'Fórmula copiada al portapapeles', 3);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = formula;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
        newToast('SUCCESS', 'Fórmula copiada al portapapeles', 3);
      } catch {
        newToast('ERROR', 'No se pudo copiar la fórmula', 4);
      }
      document.body.removeChild(ta);
    }
  };

  return (
    <div className='formula-builder'>
      <div className='fb-toolbar'>
        <div className='fb-ops'>
          {OPERATORS.map((op) => (
            <Button
              key={op.label}
              text={op.label}
              hint={(op as { hint?: string }).hint}
              stylingMode='outlined'
              onClick={() => insert(op.text, op.caretFromEnd ?? 0)}
            />
          ))}
          {showComparators && (
            <>
              <span className='fb-ops-divider' />
              {COMPARATORS.map((op) => (
                <Button key={op.label} text={op.label} stylingMode='outlined' onClick={() => insert(op.text, 0)} />
              ))}
            </>
          )}
        </div>
        <div className='fb-toolbar-right'>
          <SelectBox
            width={150}
            items={FUNCTIONS}
            displayExpr='name'
            value={null}
            placeholder='Función…'
            onValueChanged={(e) => {
              if (e.value) {
                insert(e.value.tpl, e.value.caretFromEnd);
                e.component.option('value', null);
              }
            }}
          />
          <Button icon='copy' text='Copiar' stylingMode='text' type='default' onClick={copyFormula} />
        </div>
      </div>

      <div className='fb-variable'>
        {analytes.length > 0 && (
          <div className='fb-var-group'>
            <NumberBox
              value={searchId ?? undefined}
              placeholder='Buscar por ID'
              showSpinButtons={false}
              min={1}
              onValueChanged={(e) => setSearchId(e.value)}
              onEnterKey={searchAndInsert}
              width={140}
            />
            <Button icon='search' text='Buscar e insertar' stylingMode='contained' type='default' onClick={searchAndInsert} />
          </div>
        )}
        <div className='fb-var-group'>
          <TextBox
            value={abbr}
            placeholder='o escribe la abreviatura (ej: CREOM)'
            onValueChanged={(e) => setAbbr(e.value)}
            onEnterKey={insertVariable}
            width={240}
          />
          <Button icon='plus' text='Insertar [ ]' stylingMode='outlined' onClick={insertVariable} />
        </div>
      </div>

      <span className='fb-hint'>Puedes escribir o pegar la fórmula directamente en el recuadro; los botones de arriba son ayudas opcionales.</span>

      <textarea
        ref={taRef}
        className='fb-input'
        value={text}
        onChange={(e) => update(e.target.value)}
        placeholder='Escribe o pega la fórmula aquí, ej: ( [CREOM] * [ORINAVOL] ) / ( [CREA] * 1440 )  ·  elevación: Pow([X], 2)'
        spellCheck={false}
      />

      <div className='fb-footer'>
        <span className={`fb-balance ${balanced ? 'ok' : 'error'}`}>
          {balanced ? '✓ Paréntesis balanceados' : '✗ Paréntesis sin balancear'}
        </span>
        {variables.length > 0 && (
          <div className='fb-vars'>
            <span className='fb-vars-label'>Variables:</span>
            {variables.map((v) => (
              <span className='fb-var-chip' key={v}>
                {v}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
